import React, { useState } from 'react';
import { UseQueryState } from 'urql';
import { useRouter } from 'next/router';
import { LinkCta } from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { ApplePayButton } from 'ui/components/ApplePayButton';
import { PriceBreakdown } from 'ui/components/PriceBreakdown';
import { CartAppliedPromotions } from 'ui/components/CartAppliedPromotions';
import { AfterpayOrKlarnaText } from 'ui/components/AfterpayOrKlarnaText';
import { AlertMessage } from 'ui/elements/AlertMessage';
import { GooglePayButton } from 'ui/components/GooglePayButton';
import { isNumber } from 'utils/isNumber';
import { isCartInStock } from 'utils/isCartInStock';
import { PayPalButton } from 'ui/components/PayPalButton';
import { AmazonPayButton } from 'ui/components/AmazonPayButton';
import TermsAndConditions from 'ui/components/TermsAndConditions';
import TermsAndConditionsBackup from 'ui/components/TermsAndConditionsBackup';
import { CartQuery } from '__generated__/graphql';
import { PaymentMethod, BASKET_STOCK_STATUS } from 'utils/constants';
import {
  isLongTermPreOrder,
  isLongTermPreOrderPaymentMethod,
} from 'utils/longTermPreOrder';
import { useFeature } from 'hooks/useFeature';
import { dyCheckoutStartedEvent } from 'utils/dynamicYield';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useModalsOpenStore } from 'store';

import { AdyenApplePayButton } from '../AdyenApplePayButton';
import { isTherePromosButNotASinglePromoClaimed } from '../GiftWithPurchase/utils';
import { GWPOldDesign } from '../GiftWithPurchase/OldDesign';

import { CartProductSummary } from './CartProductSummary';
import { PromoCodeSection } from './PromoCodeSection';

type CartSummaryProps = {
  cartResult: UseQueryState<CartQuery>;
  editableItems?: boolean;
  preventItemLinks?: boolean;
  showCheckoutLinks?: boolean;
  showEstimatedDeliveryDate?: boolean;
  showHeaderLinks?: boolean;
  showItemSummary?: boolean;
  showReturnShippingAlert?: boolean;
  errorHandling?: {
    updateError?: (s: string[]) => void;
    cartUpdateError?: string[];
    cartUpdateWarnings?: string[];
  };
};

export const CartSummary = ({
  cartResult,
  editableItems,
  preventItemLinks,
  showCheckoutLinks,
  showEstimatedDeliveryDate,
  showItemSummary,
  showReturnShippingAlert,
  errorHandling,
}: CartSummaryProps) => {
  const translate = useTranslate();
  const {
    staticFeatures: { injectDynamicYieldScripts },
  } = useSiteConfig();
  const { setModalOpen } = useModalsOpenStore();

  const displayPromoDiscountBreakdown = useFeature(
    'DISPLAY_PROMO_DISCOUNT_BREAKDOWN'
  );
  const isGiftWithPurchaseNewDesign = useFeature(
    'GIFT_WITH_PURCHASE_NEW_DESIGN'
  );

  const [couponIsFetching, setCouponIsFetching] = useState<boolean>(false);

  const router = useRouter();

  const cart = cartResult.data?.activeCart;

  if (!cart) return null;

  const mixedCartNotAllowed =
    cart.basketInStockStatus === BASKET_STOCK_STATUS.MIXED &&
    cart.isMixedCartAllowed === false;

  const canProceedToPayment =
    !cart.hasOfflineProducts &&
    isCartInStock(cartResult) &&
    !couponIsFetching &&
    !mixedCartNotAllowed &&
    (!errorHandling?.cartUpdateError ||
      errorHandling?.cartUpdateError?.length <= 0) &&
    (!errorHandling?.cartUpdateWarnings ||
      errorHandling?.cartUpdateWarnings?.length <= 0);

  const { estimatedTotal, giftWithPurchases, products } = cart;

  const showAmazonPayButton =
    !cartResult.fetching &&
    !cartResult.error &&
    !mixedCartNotAllowed &&
    (!isLongTermPreOrder(cart) ||
      isLongTermPreOrderPaymentMethod(cart, PaymentMethod.AMAZON_PAY));
  const isEstimate = !router.pathname.includes('checkout/payment');

  return (
    <div data-test-id="cart-summary">
      {showItemSummary && !!products.length && (
        <CartProductSummary
          basketId={cart.id}
          editableItems={editableItems}
          preventItemLinks={preventItemLinks}
          products={cart.products}
          errorHandling={errorHandling}
          showEstimatedDeliveryDate={showEstimatedDeliveryDate}
        />
      )}
      <PromoCodeSection cart={cart} setCouponIsFetching={setCouponIsFetching} />
      {showReturnShippingAlert && (
        <div className="border-y border-puma-black-500 py-4 mb-6">
          <AlertMessage icon="loop-forward">
            {translate('returnShipping')}
          </AlertMessage>
        </div>
      )}
      {!isGiftWithPurchaseNewDesign &&
        isTherePromosButNotASinglePromoClaimed({ giftWithPurchases }) && (
          <GWPOldDesign.CalloutButton
            className="w-full mb-6"
            onClick={() => setModalOpen('GWPModal', true)}
          />
        )}

      <div id="price-breakdown-container">
        {displayPromoDiscountBreakdown && (
          <CartAppliedPromotions cart={cart} className="mb-6" />
        )}
        <PriceBreakdown cart={cart} isEstimate={isEstimate} />
        {isNumber(estimatedTotal) && (
          <div className="flex justify-end">
            <AfterpayOrKlarnaText amount={estimatedTotal} />
          </div>
        )}
        {showCheckoutLinks && (
          <div className="w-full space-y-3 mt-6">
            <TermsAndConditions
              page="tacOnCartPage"
              backupContent={<TermsAndConditionsBackup />}
            >
              <LinkCta
                href="checkout"
                bleed
                size="lg"
                dataTestId="cart-summary-checkout"
                dimmed={!canProceedToPayment}
                onClick={e => {
                  if (!canProceedToPayment) {
                    e.preventDefault();
                    return false;
                  }
                  injectDynamicYieldScripts && dyCheckoutStartedEvent();
                }}
                label={translate('checkout')}
              />

              <AdyenApplePayButton disabled={!canProceedToPayment} />
              <PayPalButton disabled={!canProceedToPayment} />
              <ApplePayButton disabled={!canProceedToPayment} />
              <GooglePayButton
                buttonType="pay"
                disabled={!canProceedToPayment}
              />
              <>
                {showAmazonPayButton && (
                  <AmazonPayButton disabled={!canProceedToPayment} />
                )}
              </>
            </TermsAndConditions>
          </div>
        )}
      </div>
    </div>
  );
};
